import { all, contains, find, findIndex, flatten, forEach } from 'ramda';

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Test whether a string is a uuid or not.
 * @param {String} id
 */
export function isUuid(id) {
  return uuidRegex.test(id);
};

function pathToRegExp(query) {
  if (query instanceof RegExp) return query;
  query = query.replace(/\*/g, '(.*)').replace(/^\"|\"$/g, '');
  return new RegExp('^' + query + '$', 'i');
};

const re = {
  bareName: '^([^!%\[#:]*)*',    // Match the name, up to % or #
  type: '(?:\%([^#!:]*))*',      // Match the type
  filter: '(?::([^#:]*))*',     // Any filter?
  evalPlug: '(?:\!([^#\[]*))*',  // Evaluated plug name
  plugName: '(?:\#([^\[]*))*',  // Plug name
  attrs: '(.*)',                 // Any attribute selectors (entire match)
};

const queryRegex = new RegExp(
  re.bareName + re.type + re.filter + re.evalPlug + re.plugName + re.attrs
);

const queryRegexNoName = new RegExp(
  '()' + re.type + re.filter + re.evalPlug + re.plugName + re.attrs
);

const attrsRegex = /(?:\[([^\]]*)\])/g;

/*
 * Parse "[translation][name='Transform']" and turn it into
 * operator/property/properties fields for matching.
 */
function parseOperatorAttributes(attrsString) {
  // Take "[translation][name='Transform']" and convert it into
  // a filter function that will match the specific operator
  var items, match;

  let property;
  let properties;

  while ((match = attrsRegex.exec(attrsString)) !== null) {
    items = match[1].split('=');
    if (items.length === 2) {
      if (!properties) properties = {};
      properties[items[0]] = items[1];
    } else {
      property = items[0];
    }
  }

  return { property, properties };
};

function buildQueryObjectFromString(query) {
  // Support looking for the active scene node with '~'.
  const scene = query[0] === '~';
  const quotedIdx = !scene && query[0] === '"' && query.slice(1).indexOf('"');
  const quotedName = quotedIdx && quotedIdx !== -1;

  const matches = (quotedName || scene) ?
      queryRegexNoName.exec(query.slice(scene ? 1 : quotedIdx + 2)) :
      queryRegex.exec(query);
  const name = quotedName ? query.slice(1, quotedIdx + 1) : (scene ? false : matches[1]);
  const type = matches[2];
  const filter = matches[3];
  const evalPlug = matches[4];
  const plug = matches[5];
  const attrs = matches[6];
  const id = isUuid(name) ? name : undefined;
  return Object.assign({ scene, id, name: (id || !name) ? undefined : pathToRegExp(name),
    type, filter, evalPlug, plug, }, parseOperatorAttributes(attrs), { includeParent: true });
};

/**
 * Takes a query string, and returns an object with the attributes we are looking
 * for.
 */
export function buildQueryObject(query) {
  if (typeof query === 'string') return buildQueryObjectFromString(query);
  if (typeof query === 'object') {
    //if (typeof query.name === 'string' && query.name.indexOf('*') !== -1)
    return Object.assign({}, query, {
      name: query.name ? pathToRegExp(query.name) : undefined,
      names: (query.names && Array.isArray(query.names)) ? query.names.map(pathToRegExp) : undefined,
      // include parent by default if no from query is specified
      includeParent: query.includeParent === undefined ? !query.from : query.includeParent,
    });
  }

  return {};
}

/*
 * Given a QueryObject, will return a matching path for this node, or
 * null if the node does not match.
 */
export function getMatch(node, queryObj, onlyNode) {
  var nodeMatch = getNodeMatch(node, queryObj);
  return !nodeMatch ? null : (onlyNode ? nodeMatch.id : matchInside(node, queryObj));
}

/*
 * Given a QueryObject, will return the node if the node matches the
 * node query parameters (id, name, type).
 */
function getNodeMatch(node, queryObj) {
  if (queryObj.id !== undefined && queryObj.id !== node.id) return null;
  if (queryObj.name !== undefined && !queryObj.name.test(node.name)) return null;
  if (queryObj.type !== undefined) {
    if (typeof queryObj.type === 'string') {
      if (queryObj.type !== node.type) return null;
    } else if (Array.isArray(queryObj.type)) {
      let found = false
      if (contains(node.type, queryObj.type)) found = true;
      else if(queryObj.type.indexOf && queryObj.type.indexOf(node.type)>=0) found = true;

      if(!found) return null;
    }
  }
  if (queryObj.hasPlug !== undefined && !node.plugs[queryObj.hasPlug]) return null;

  return node;
};

function matchInside(node, queryObj) {
  const { properties, property, operator, operatorIndex } = queryObj;

  if (queryObj.evalPlug) {
    return property ? [node.id, 'eval', queryObj.evalPlug, property] :
      [node.id, 'eval', queryObj.evalPlug];
  }

  if (!queryObj.plug) {
    if (property === 'name' || property === 'type') {
      return [node.id, property];
    }

    return node.id;
  }

  const plug = node.plugs[queryObj.plug];
  if (!plug) return null;

  const propKeys = properties && Object.keys(properties);

  if (!properties  && !property && !operator && operatorIndex === undefined) {
    return node.id; // No operator query provided.
  }

  const idx = queryObj.operatorIndex !== undefined ? queryObj.operatorIndex :
    findIndex((op) => {
      return (property ? op.hasOwnProperty(property) : true) &&
        (properties ?  all((k) => op[k] === properties[k], propKeys) : true);
    }, plug);

  if (idx === -1) return null;
  return property ? [node.id, queryObj.plug, idx, property] :
    [node.id, queryObj.plug, idx];
};

export function matchChildren(store, node, queryObj, parentNodeToMatch) {
  let results = [];
  const sceneGraph = store.get('sceneGraph');

  const parentMatch = parentNodeToMatch && getMatch(parentNodeToMatch, queryObj);
  if (parentMatch) results.push(parentMatch);

  forEach((childId) => {
    const childNode = sceneGraph.nodes[childId];
    if (!childNode) throw new Error(`Invalid child for '${node.get('name')}' - ${childId}`);

    const match = getMatch(childNode, queryObj);
    if (match) results.push(match);
    if (!queryObj.shallow) {
      results = results.concat(matchChildren(store, childNode, queryObj));
    }
  }, node.children || []);

  return results;
};

export function findChild(store, node, queryObj, parentNodeToMatch, onlyNode) {
  let result;
  if (parentNodeToMatch) {
    result = getMatch(parentNodeToMatch, queryObj, onlyNode);
    if (result) return result;
  }

  const sceneGraph = store.get('sceneGraph');
  const query = Object.assign({}, queryObj);
  if (query.names) {
    query.name = query.names[0]
    query.names = query.names.slice(1);
  }

  const match = find((childId) => {
    const childNode = sceneGraph.nodes[childId];
    result = getMatch(childNode, query, onlyNode || (query.names && query.names.length));
    if (query.names) { // heirarchical search by name
      if (!result || !query.names.length) return !!result;
      result = findChild(store, childNode, query, null, onlyNode);
      if (result) return true;
    } else {
      if (result) return true;
      result = !query.shallow && findChild(store, childNode, query, null, onlyNode);
      if (result) return true;
    }
  }, node.children || []);
  return match ? result : null;
};




// WEBPACK FOOTER //
// ./src/modules/utils/selectors.js